import { fetchTeamMembers } from '@/apis/fetchTeamMembers';
import { fetchTeams, fetchTeam } from '@/apis/fetchTeams';
import useOrganizationStore from '@/state/orgState';
import useTeamStore from '@/state/teamState';
import { useQuery } from '@tanstack/react-query';
import { useUserLogged } from './useUserLogged';

const useFetchTeams = (orgUUID?: string) => {
  /*
   * Current user's org comes from useUserLogged, never the Zustand org store.
   * Two bugs in one line before this:
   *
   * 1. The store won over the explicit argument, so the internal leaderboard
   *    at /leaderboard/<orgUUID> asked for org 3150ad5c… and the request went
   *    out for 3c5a8f08…, returning `{"Teams":[],"Error":null}` and rendering
   *    "No teams found" on an org that has four teams. The argument now wins;
   *    the fallback only applies to callers that pass nothing (or '').
   * 2. The fallback read the org store directly, which is what held the
   *    foreign org. useUserLogged is the single source of truth for the
   *    logged-in user's org.
   */
  const { organization } = useUserLogged();
  const resolvedOrgUUID = orgUUID || organization?.OrgUUID;

  const { data, isLoading, error } = useQuery({
    queryKey: ['teams', resolvedOrgUUID],
    enabled: !!resolvedOrgUUID,
    queryFn: async () => {
      const result = await fetchTeams({
        OrgUUID: resolvedOrgUUID!,
      });
      // fetchTeams swallows failures (a 403, a network error) and returns
      // undefined. Returning that as query data made React Query throw
      // "Query data cannot be undefined" and left callers with no `error`,
      // so surface the failure as one instead. An empty org still returns [].
      if (!result || !Array.isArray(result.Teams)) {
        throw new Error(result?.Error || 'Failed to load teams');
      }
      return result.Teams;
    },
  });

  return { data, isLoading, error };
};

const useFetchTeam = (TeamUUID: string, OrgUUID: string) => {
  const setTeam = useTeamStore((state) => state.setTeam);
  const { data, isLoading, error } = useQuery({
    queryKey: ['team', TeamUUID],
    enabled: !!TeamUUID && !!OrgUUID,
    queryFn: async () => {
      const result = await fetchTeam({
        TeamUUID,
        OrgUUID,
      });
      if (result?.Team) {
        setTeam(result.Team);
        return result;
      }
      return null;
    },
  });

  return { data, isLoading, error };
};

export const useFetchTeamMembers = (TeamUUID: string) => {
  const { user } = useUserLogged();
  const organization = useOrganizationStore((state) => state.organization);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['teamMembers', TeamUUID],
    enabled: !!TeamUUID && !!user?.OrganizationUUID,
    queryFn: async () => {
      const result = await fetchTeamMembers({
        TeamUUID: TeamUUID,
        OrgUUID: user?.OrganizationUUID ?? '',
      });
      return result?.Members;
    },
  });

  return { data, isLoading, error, refetch };
};

export default useFetchTeams;
